/*
<summary>
   This functional component is all about rendering the User Created Inspection execution
   screen — a multi-tab view with a Check Items panel grouped by header, an NG checkbox +
   reason per item, and a Start/Pause/Complete session timer. 
   Developer: Aashish Singh, Created Date: 27-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { Accordion, Button, Form } from 'react-bootstrap';
import { ExclamationCircleFill } from 'react-bootstrap-icons';
import { FormattedMessage } from 'react-intl';
import toast from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';
import authStore from '../../../core/stores/auth-store';
import { useStore } from '../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import InspectionStatusEnum from '../../../constants/enums/inspection-status-enum';
import UsageStatusEnum from '../../../constants/enums/usage-status-enum';
import { IToolCheckItem, IToolInspectionTab } from '../../../models/forms/IToolInspection';
import { IToolManagementVM } from '../../../models/response/IToolManagementResponse';
import { IToolCheckItemData } from '../../../models/response/IUserInspectedListResponse';
import RoutesConstants from '../../../shared-components/routes/routes-constants';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import worker_script from '../../../shared-components/web-worker/webWorker';
import { formatMessage } from '../../../translations/format-message';
import noImageIcon from '../../../assets/Images/filterNodata.png';
import './user-created-inspection.css';

const timerWorker = new Worker(worker_script);

interface ILocalCheckItem {
  ApiId: number;
  HeaderTitle: string;
  HeaderOrder: number;
  CheckItemMessage: string;
  ItemOrder: number;
  IsChecked: boolean;
  AllowTextEntry: boolean;
  InfoText: string;
}

interface ILocalHeader {
  Title: string;
  HeaderOrder: number;
  Items: ILocalCheckItem[];
}

interface ILocalTab {
  TabIndex: number;
  Title: string;
  Images: IToolInspectionTab['Images'];
  Headers: ILocalHeader[];
}

/**
* Builds the tab/header/check-item view model from the tool's own InspectionTabs config.
*/
const buildTabsFromTemplate = (tabs: IToolInspectionTab[]): ILocalTab[] =>
  (tabs || []).map((tab) => {
    const headerGroups =
      Array.isArray(tab.Headers) && tab.Headers.length > 0
        ? tab.Headers
        : [{ Title: 'Default', HeaderOrder: 0, Items: tab.CheckItems || [] }];

    return {
      TabIndex: tab.TabIndex,
      Title: tab.Title,
      Images: tab.Images || [],
      Headers: headerGroups.map((header, headerIdx) => ({
        Title: header.Title || 'Default',
        HeaderOrder: header.HeaderOrder ?? headerIdx,
        Items: (header.Items || []).map((item: IToolCheckItem, itemIdx: number) => ({
          ApiId: 0,
          HeaderTitle: header.Title || 'Default',
          HeaderOrder: header.HeaderOrder ?? headerIdx,
          CheckItemMessage: item.CheckItemMessage,
          ItemOrder: itemIdx,
          IsChecked: false,
          AllowTextEntry: !!item.AllowTextEntry,
          InfoText: '',
        })),
      })),
    };
  });

/**
* Renders the User Created Inspection execution screen - tab bar, Check Items accordion,
* and Start/Pause/Complete session timer.
*/
const UserCreatedInspection = () => {
  const history = useHistory();
  const { toolId } = useParams<{ toolId: string }>();
  const { toolManagementStore, equipmentInspectionStore, factoryStore } = useStore();
  const { toolManagementDetails, toolManagementState, GetToolManagementByIdService } = toolManagementStore;
  const { allAvailableFactories, GetAllFactoriesService } = factoryStore;
  const {
    GetUserCreatedInspectionByToolIdService,
    AddUserCreatedInspectionService,
    userCreatedInspectionDetail,
    getUserCreatedInspectionState,
    addUserCreatedInspectionState,
  } = equipmentInspectionStore;

  const [row, setRow] = useState<IToolManagementVM | undefined>(undefined);
  const [tabs, setTabs] = useState<ILocalTab[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [webWorkerTime, setWebWorkerTime] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [toolMasterCheckInfoId, setToolMasterCheckInfoId] = useState<number | null>(null);
  const [sessionStartDate, setSessionStartDate] = useState<string>('');
  // NG is one flag for the whole session, not per item.
  const [isOverallNg, setIsOverallNg] = useState(false);
  const [overallNgReasonText, setOverallNgReasonText] = useState('');
  // Refs so the history.block callback below (registered once) always reads the latest
  // isTimerRunning value / handlePauseTimer closure instead of a stale one from mount.
  const isTimerRunningRef = useRef(isTimerRunning);
  const handlePauseTimerRef = useRef<() => Promise<boolean | undefined>>();
  const currentToolIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    GetAllFactoriesService(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ownerFactoryName =
    allAvailableFactories.find((option) => option.id === Number(row?.OwnerFactoryId))?.value || '-';

  useEffect(() => {
    // Always fetch this tool by id - works for both a direct link and "Perform Action". Also
    // reset any in-progress session state - react-router does not remount this component
    // just because the :toolId route param changes, so without this a stale session from a
    // previously-viewed tool could otherwise leak into this one.
    setRow(undefined);
    setTabs([]);
    setActiveTabIndex(0);
    setWebWorkerTime(0);
    setIsTimerRunning(false);
    setToolMasterCheckInfoId(null);
    setSessionStartDate('');
    setIsOverallNg(false);
    setOverallNgReasonText('');
    equipmentInspectionStore.reset();
    timerWorker.postMessage({ turn: 'reset' });
    if (toolId) {
      GetToolManagementByIdService(Number(toolId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId]);

  useEffect(() => {
    currentToolIdRef.current = toolId ? Number(toolId) : undefined;
  }, [toolId]);

  useEffect(() => {
    if (toolManagementDetails && toolManagementDetails.ToolId === Number(toolId)) {
      setRow(toolManagementDetails);
      setTabs(buildTabsFromTemplate(toolManagementDetails.InspectionTabs || []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolManagementDetails]);

  useEffect(() => {
    // Inspected -> this is a fresh inspection, start blank. NotInspected -> a prior session
    // may exist (paused) or may not (never started) - fetch it either way and hydrate below
    // if found.
    if (row && row.InspectionStatus === InspectionStatusEnum.NotInspected) {
      GetUserCreatedInspectionByToolIdService(row.ToolId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row]);

  // Merge a previously paused session's check/NG state into the tabs, matched by
  // TabIndex + Header + CheckItemText.
  useEffect(() => {
    if (
      !userCreatedInspectionDetail ||
      userCreatedInspectionDetail.State === 3 ||
      userCreatedInspectionDetail.ToolId !== row?.ToolId
    ) {
      return;
    }
    setToolMasterCheckInfoId(userCreatedInspectionDetail.Id);
    setSessionStartDate(userCreatedInspectionDetail.StartDate);
    setWebWorkerTime(userCreatedInspectionDetail.Duration || 0);
    // InspectionResult is submitted as (isOverallNg ? 0 : 1)
    setIsOverallNg(userCreatedInspectionDetail.InspectionResult === 0);
    setOverallNgReasonText(userCreatedInspectionDetail.NgReason || '');

    const fetchedItems = userCreatedInspectionDetail.ToolCheckItemDataList || [];
    if (fetchedItems.length === 0) return;

    setTabs((prevTabs) =>
      prevTabs.map((tab) => ({
        ...tab,
        Headers: tab.Headers.map((header) => ({
          ...header,
          Items: header.Items.map((item) => {
            const match = fetchedItems.find(
              (fi) =>
                fi.TabIndex === tab.TabIndex &&
                fi.HeaderName?.trim().toLowerCase() === header.Title.trim().toLowerCase() &&
                fi.CheckItemText?.trim() === item.CheckItemMessage.trim()
            );
            if (!match) return item;
            return {
              ...item,
              ApiId: match.Id,
              IsChecked: match.IsCheckItemChecked,
              InfoText: item.AllowTextEntry ? match.Info || '' : item.InfoText,
            };
          }),
        })),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCreatedInspectionDetail]);

  useEffect(() => {
    timerWorker.onmessage = ({ data: { time } }: any) => {
      setWebWorkerTime(time);
    };
    return () => {
      timerWorker.postMessage({ turn: 'off' });
    };
  }, []);

  const formatTime = (seconds: number) => {
    const totalSeconds = Math.floor(seconds);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return [hrs, mins, secs].map((v) => String(v).padStart(2, '0')).join(':');
  };

  /**
  * Flattens the tabs into the shape AddUserCreatedInspection expects. `freshItems` lets a
  * caller resolve real Ids right after a Pause call, since `tabs` state won't have them yet.
  */
  const buildCheckItemPayload = (freshItems?: IToolCheckItemData[]) => {
    if (!row) return [];
    return tabs.flatMap((tab) =>
      tab.Headers.flatMap((header) =>
        header.Items.map((item) => {
          const freshMatch = freshItems?.find(
            (fi) =>
              fi.TabIndex === tab.TabIndex &&
              fi.HeaderName?.trim().toLowerCase() === header.Title.trim().toLowerCase() &&
              fi.CheckItemText?.trim() === item.CheckItemMessage.trim()
          );
          return {
            Id: freshMatch ? freshMatch.Id : item.ApiId || 0,
            ToolId: row.ToolId,
            ToolMasterCheckInfoId: toolMasterCheckInfoId || 0,
            TabIndex: tab.TabIndex,
            HeaderName: header.Title,
            HeaderOrder: String(header.HeaderOrder ?? 0),
            CheckItemText: item.CheckItemMessage,
            IsCheckItemChecked: item.IsChecked,
            Info: item.AllowTextEntry ? item.InfoText || null : null,
            ItemOrder: item.ItemOrder,
            CreatedBy: authStore.getUserId,
            UpdatedBy: authStore.getUserId,
          };
        })
      )
    );
  };

  // NG checked -> InspectionResult 0; unchecked -> 1.
  const overallInspectionResult = isOverallNg ? 0 : 1;
  const overallNgReason = isOverallNg ? overallNgReasonText || '' : '';

  const handleStartTimer = async () => {
    if (!row) return;

    if (row.UsageStatus === UsageStatusEnum.InUse) {
      toast.error(formatMessage('ToolInUse'));
      return;
    }

    if (toolMasterCheckInfoId) {
      timerWorker.postMessage({ turn: 'on', initialTime: webWorkerTime });
      setIsTimerRunning(true);
      return;
    }
    const startedForToolId = row.ToolId;

    await GetUserCreatedInspectionByToolIdService(startedForToolId);
    if (currentToolIdRef.current !== startedForToolId) return;
    const existingSession = equipmentInspectionStore.userCreatedInspectionDetail;
    if (existingSession && existingSession.State !== 3 && existingSession.ToolId === startedForToolId) {
      setToolMasterCheckInfoId(existingSession.Id);
      setSessionStartDate(existingSession.StartDate);
      setWebWorkerTime(existingSession.Duration || 0);
      timerWorker.postMessage({ turn: 'on', initialTime: existingSession.Duration || 0 });
      setIsTimerRunning(true);
      return;
    }

    setIsOverallNg(false);
    setOverallNgReasonText('');

    const now = new Date().toISOString();
    const started = await AddUserCreatedInspectionService({
      ToolId: startedForToolId,
      ToolNumber: row.ToolManagementNo,
      StartDate: now,
      State: 1,
      Duration: 0,
      InspectionResult: 1,
      CreatedBy: authStore.getUserId,
      ToolCheckItemDataDetailsList: buildCheckItemPayload(),
      TenantId: Number(getTenantIdFromLocalStorage()),
    });
    if (!started || currentToolIdRef.current !== startedForToolId) return;

    setSessionStartDate(now);
    // Start doesn't return the new record's Id, so re-fetch to get it. Read the store's
    // freshly-fetched value directly so toolMasterCheckInfoId is guaranteed to be
    // set before this function returns — otherwise a fast Start-then-Complete click could
    // race the effect and leave Complete silently unable to do anything.
    await GetUserCreatedInspectionByToolIdService(startedForToolId);
    if (currentToolIdRef.current !== startedForToolId) return;
    const freshSession = equipmentInspectionStore.userCreatedInspectionDetail;
    setToolMasterCheckInfoId(
      freshSession && freshSession.ToolId === startedForToolId ? freshSession.Id : null
    );
    timerWorker.postMessage({ turn: 'on', initialTime: webWorkerTime });
    setIsTimerRunning(true);
  };

  const handlePauseTimer = async () => {
    timerWorker.postMessage({ turn: 'off' });
    setIsTimerRunning(false);
    isTimerRunningRef.current = false;
    if (!row || !toolMasterCheckInfoId) return false;

    const paused = await AddUserCreatedInspectionService({
      ToolId: row.ToolId,
      ToolNumber: row.ToolManagementNo,
      ToolMasterCheckInfoId: toolMasterCheckInfoId,
      StartDate: sessionStartDate,
      Duration: webWorkerTime,
      State: 2,
      InspectionResult: overallInspectionResult,
      NgReason: overallNgReason || null,
      CreatedBy: authStore.getUserId,
      UpdatedBy: authStore.getUserId,
      ToolCheckItemDataDetailsList: buildCheckItemPayload(),
      TenantId: Number(getTenantIdFromLocalStorage()),
    });
    if (paused) {
      // Sync back the real Ids assigned to any newly-created check items.
      await GetUserCreatedInspectionByToolIdService(row.ToolId);
      toast.success(formatMessage('inspection_paused_successfully'));
    }
    return paused;
  };

  // Always keep the ref pointed at the latest handlePauseTimer closure (it captures row,
  // toolMasterCheckInfoId, sessionStartDate etc., all of which change after mount).
  useEffect(() => {
    handlePauseTimerRef.current = handlePauseTimer;
  });

  useEffect(() => {
    isTimerRunningRef.current = isTimerRunning;
  }, [isTimerRunning]);

  // Intercepts every navigation attempt away from this page (sidebar/module links, browser
  // back/forward, our own history.push calls) while a session is running. On confirming the
  // native "leave page?" prompt, pauses the session first, then completes the navigation the
  // user originally requested - registered once (via refs) so it never fires the pause with
  // stale state.
  useEffect(() => {
    const unblock = history.block((location) => {
      if (!isTimerRunningRef.current) return undefined;
      const confirmed = window.confirm(formatMessage('exit_warning'));
      if (!confirmed) return false;

      isTimerRunningRef.current = false;
      unblock();
      Promise.resolve(handlePauseTimerRef.current?.()).finally(() => {
        history.push(location.pathname + location.search);
      });
      return false;
    });
    return () => unblock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
  * Back button handler - pauses the in-progress session before navigating away if a timer is
  * currently running, so an in-progress inspection isn't lost silently; navigates immediately
  * if no session is active. Marks isTimerRunningRef false first so the history.block listener
  * above doesn't also intercept this already-confirmed navigation.
  */
  const handleBackClick = async () => {
    if (isTimerRunning) {
      await handlePauseTimer();
    }
    isTimerRunningRef.current = false;
    history.push(RoutesConstants.ToolManagement);
  };

  const handleComplete = async () => {
    if (!row) return;
    if (!toolMasterCheckInfoId) {
      // The Complete/Pause buttons are also disabled until toolMasterCheckInfoId is set
      // (see the isDisabled checks below), so this should be unreachable — but returning
      // silently here previously left the timer running with zero feedback whenever it
      // did happen.
      toast.error(formatMessage('inspection_session_not_ready'));
      return;
    }
    if (!allTabsComplete && !isOverallNg) {
      toast.error(formatMessage('please_check_all_items'));
      return;
    }
    timerWorker.postMessage({ turn: 'off' });
    setIsTimerRunning(false);

    // Complete can only update existing check items, not create them. If Start->Complete
    // happened with no Pause in between, items still have Id 0 - persist them first.
    let payload = buildCheckItemPayload();
    if (payload.some((item) => item.Id === 0)) {
      const saved = await AddUserCreatedInspectionService({
        ToolId: row.ToolId,
        ToolNumber: row.ToolManagementNo,
        ToolMasterCheckInfoId: toolMasterCheckInfoId,
        StartDate: sessionStartDate,
        Duration: webWorkerTime,
        State: 2,
        InspectionResult: overallInspectionResult,
        NgReason: overallNgReason || null,
        CreatedBy: authStore.getUserId,
        UpdatedBy: authStore.getUserId,
        ToolCheckItemDataDetailsList: payload,
        TenantId: Number(getTenantIdFromLocalStorage()),
      });
      if (!saved) return;
      await GetUserCreatedInspectionByToolIdService(row.ToolId);
      payload = buildCheckItemPayload(equipmentInspectionStore.userCreatedInspectionDetail?.ToolCheckItemDataList);
    }

    const now = new Date().toISOString();
    const completed = await AddUserCreatedInspectionService({
      ToolId: row.ToolId,
      ToolNumber: row.ToolManagementNo,
      ToolMasterCheckInfoId: toolMasterCheckInfoId,
      StartDate: sessionStartDate,
      EndDate: now,
      Duration: webWorkerTime,
      State: 3,
      InspectionResult: overallInspectionResult,
      NgReason: overallNgReason || null,
      CreatedBy: authStore.getUserId,
      UpdatedBy: authStore.getUserId,
      ToolCheckItemDataDetailsList: payload,
      TenantId: Number(getTenantIdFromLocalStorage()),
    });
    if (completed) {
      toast.success(formatMessage('inspection_completed_successfully'));
      isTimerRunningRef.current = false;
      history.push(RoutesConstants.ToolManagement);
    }
  };

  const handleItemCheckedToggle = (headerTitle: string, itemOrder: number, checked: boolean) => {
    setTabs((prev) =>
      prev.map((tab, tIdx) =>
        tIdx !== activeTabIndex
          ? tab
          : {
              ...tab,
              Headers: tab.Headers.map((header) =>
                header.Title !== headerTitle
                  ? header
                  : {
                      ...header,
                      Items: header.Items.map((item) =>
                        item.ItemOrder !== itemOrder ? item : { ...item, IsChecked: checked }
                      ),
                    }
              ),
            }
      )
    );
  };

  const handleItemInfoTextChange = (headerTitle: string, itemOrder: number, value: string) => {
    setTabs((prev) =>
      prev.map((tab, tIdx) =>
        tIdx !== activeTabIndex
          ? tab
          : {
              ...tab,
              Headers: tab.Headers.map((header) =>
                header.Title !== headerTitle
                  ? header
                  : {
                      ...header,
                      Items: header.Items.map((item) =>
                        item.ItemOrder !== itemOrder ? item : { ...item, InfoText: value }
                      ),
                    }
              ),
            }
      )
    );
  };

  const handleOverallNgToggle = () => {
    setIsOverallNg((prev) => {
      if (prev) setOverallNgReasonText('');
      return !prev;
    });
  };

  const activeTab = tabs[activeTabIndex];
  const isTabIncomplete = (tab: ILocalTab) =>
    tab.Headers.some((header) => header.Items.some((item) => !item.IsChecked));
  const allTabsComplete = tabs.length > 0 && !tabs.some((tab) => isTabIncomplete(tab));

  const buttons = isTimerRunning
    ? [
        {
          labelKey: 'pause',
          className: 'btn-secondary',
          onClick: handlePauseTimer,
          isDisabled: !toolMasterCheckInfoId,
        },
        {
          labelKey: 'complete',
          className: 'btn-primary',
          onClick: handleComplete,
          isDisabled: (!allTabsComplete && !isOverallNg) || !toolMasterCheckInfoId,
        },
      ]
    : [
        {
          labelKey: 'start',
          className: 'btn-success',
          onClick: handleStartTimer,
          isDisabled: !row,
        },
      ];

  return (
      <div className="uci-page uci-user-created-page">
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col">
            <div className="page_title_wrap">
              <div className="page_title_wrap">
                <ul className="add-list operator-back">
                  <li className="process-chart">
                    <Button
                      title={formatMessage('operator_list_detail')}
                      className="btn btn-secondary"
                      onClick={handleBackClick}
                    >
                      <FormattedMessage id="back" />
                    </Button>
                  </li>
                  <li>
                    <h1 className="page_title"><FormattedMessage id="equipment_inspection_user_created" /> </h1>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(toolManagementState.inProgress ||
        getUserCreatedInspectionState.inProgress ||
        addUserCreatedInspectionState.inProgress) && (
        <FormLoader
          loading={
            toolManagementState.inProgress ||
            getUserCreatedInspectionState.inProgress ||
            addUserCreatedInspectionState.inProgress
          }
        />
      )}

      <div className="card card-page uci-card position-relative">
        <div className="row g-1 uci-info-row justify-space-between p-0 mb-1">
          <div
            className="col-3"
            title={row?.ToolManagementNo || ''}
          >
            <p className="small text-muted"><FormattedMessage id="management_no" /></p>
            <p className="text-dark uci-info-value">{row?.ToolManagementNo || '-'}</p>
          </div>
          <div
            className="col-3"
            title={row?.ToolName || ''}
          >
            <p className="small text-muted"><FormattedMessage id="tool_name_equipment_name" /></p>
            <p className="text-dark uci-info-value">{row?.ToolName || '-'}</p>
          </div>
          <div
            className="col-3"
            title={ownerFactoryName}
          >
            <p className="small text-muted"><FormattedMessage id="owner_factory" /></p>
            <p className="text-dark uci-info-value">{ownerFactoryName}</p>
          </div>
          <div
            className="col-3"
            title={row?.ToolManufacturer || ''}
          >
            <p className="small text-muted"><FormattedMessage id="tool_manufacturer" /></p>
            <p className="text-dark uci-info-value">{row?.ToolManufacturer || '-'}</p>
          </div>
          <div
            className="col-3"
            title={row?.ToolModel || ''}
          >
            <p className="small text-muted"><FormattedMessage id="tool_model" /></p>
            <p className="text-dark uci-info-value">{row?.ToolModel || '-'}</p>
          </div>
          <div
            className="col-3"
            title={row?.SerialNo || ''}
          >
            <p className="small text-muted"><FormattedMessage id="serial_no" /></p>
            <p className="text-dark uci-info-value">{row?.SerialNo || '-'}</p>
          </div>
          <div
            className="col-3"
            title={row?.EquipmentCategory || ''}
          >
            <p className="small text-muted"><FormattedMessage id="equipment_category" /></p>
            <p className="text-dark uci-info-value">{row?.EquipmentCategory || '-'}</p>
          </div>
        </div>

        {tabs.length > 0 ? (
          <>
            <div className="operator-tabs-dimesnion position-relative">
              <ul
                className="nav nav-tabs"
                role="tablist"
              >
                {tabs.map((tab, index) => (
                  <li
                    key={tab.TabIndex}
                    className={`textTabBlock${index === activeTabIndex ? 'active textTabBlockactive' : ''}`}
                    onClick={() => setActiveTabIndex(index)}
                  >
                    <button
                      type="button"
                      className="nav-link d-flex align-items-center gap-1"
                    >
                      <span>{tab.Title}</span>
                      {isTimerRunning && isTabIncomplete(tab) && (
                        <ExclamationCircleFill
                          className="text-danger"
                          title={formatMessage('please_check_all_items')}
                        />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="row g-3">
              <div className="col-lg-8">
                <div className="uci-image-box">
                  {activeTab?.Images && activeTab.Images.some((img) => img.PreviewUrl) ? (
                    activeTab.Images.filter((img) => img.PreviewUrl).map((img, imgIdx) => (
                      <img
                        key={img.FileId || img.Id || imgIdx}
                        src={img.PreviewUrl}
                        alt={activeTab.Title}
                      />
                    ))
                  ) : (
                    <img
                      src={noImageIcon}
                      alt=""
                      className="uci-no-image"
                    />
                  )}
                </div>
              </div>
              <div className="col-lg-4">
                <div className="uci-check-items-panel">
                  <div className="uci-check-items-panel-header">{formatMessage('check_items')}</div>
                  <div className="operator_check_items">
                    <Accordion
                      key={activeTabIndex}
                      className="w-100"
                      alwaysOpen
                      defaultActiveKey={(activeTab?.Headers || []).map((header) => header.Title)}
                    >
                      {(activeTab?.Headers || []).map((header) => (
                        <Accordion.Item
                          key={header.Title}
                          eventKey={header.Title}
                          className="my-2 p-1"
                        >
                          <Accordion.Header>
                            <span className="operator-checkitems-list-title p-1">{header.Title}</span>
                          </Accordion.Header>
                          <Accordion.Body>
                            {header.Items.map((item) => (
                              <div
                                key={item.ItemOrder}
                                className="mb-1"
                              >
                                <div className="row g-1 row-operator-check-item">
                                  <div className="col-auto">
                                    <div className="d-flex">
                                      <Form.Check
                                        type="checkbox"
                                        id={`uci-check-${activeTabIndex}-${header.Title}-${item.ItemOrder}`}
                                        label={item.CheckItemMessage}
                                        checked={item.IsChecked}
                                        disabled={!isTimerRunning}
                                        onChange={(e) =>
                                          handleItemCheckedToggle(header.Title, item.ItemOrder, e.target.checked)
                                        }
                                        className="m-0"
                                      />
                                    </div>
                                  </div>
                                  <div className="col">
                                    {item.AllowTextEntry && (
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={item.InfoText}
                                        disabled={!isTimerRunning}
                                        onChange={(e) =>
                                          handleItemInfoTextChange(header.Title, item.ItemOrder, e.target.value)
                                        }
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </div>

                  <div className="uci-ng-overall-row">
                    <Form.Check
                      type="checkbox"
                      id="uci-overall-ng"
                      label={formatMessage('ng')}
                      className="uci-ng-checkbox"
                      checked={isOverallNg}
                      disabled={!isTimerRunning}
                      onChange={handleOverallNgToggle}
                    />
                    {isOverallNg && (
                      <input
                        type="text"
                        className="form-control mt-2"
                        placeholder={formatMessage('ng_reason_placeholder')}
                        value={overallNgReasonText}
                        disabled={!isTimerRunning}
                        onChange={(e) => setOverallNgReasonText(e.target.value)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="message">
            <div className="d-flex message-area">
              <div className="no-data-img">
                <img
                  src={noImageIcon}
                  alt=""
                  className="no_message"
                />
              </div>
              <div>
                <h3 className="fw-bold display-6">
                  <FormattedMessage id="no_record" />
                </h3>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="uci-timer-bar">
        <div className="row align-items-center g-0 w-100">
          <div className="col-auto">
            <div className="uci-timer-group d-flex">
              <label className="badge bg-primary fw-medium">
                <FormattedMessage id="duration" />
              </label>
              <div className="d-flex align-items-center">
                <div className="uci-timer-value">
                  <p>{formatTime(webWorkerTime)}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="col">
            <div className="d-flex gap-1 gap-xxl-1 flex-wrap justify-content-end">
              {buttons.map((btn) => (
                <div
                  className={`uci-btn-wrap ${btn.isDisabled ? 'cursor-not-allowed' : ''}`}
                  key={btn.labelKey}
                >
                  <button
                    type="button"
                    className={`btn ${btn.className} ${btn.isDisabled ? 'disabled' : ''}`}
                    disabled={btn.isDisabled}
                    onClick={btn.onClick}
                  >
                    <FormattedMessage id={btn.labelKey} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>
  );
};

export default observer(UserCreatedInspection);
